(() => {
  const { cartUrl, cartAddUrl, cartChangeUrl, cartUpdateUrl } = document.body.dataset;

  class CartError extends Error {}

  async function request(url, options, json = false) {
    const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (json) headers['Content-Type'] = 'application/json';
    const response = await fetch(url, { ...options, headers });
    const body = await response.json();
    // Shopify answers a refused line (sold out, limit reached) with 422 and a text for the customer.
    if (!response.ok) throw new CartError(body.description || body.message || '');
    return body;
  }

  // Shopify's money formats: placeholder → decimals, thousands separator, decimal separator.
  const MONEY_STYLES = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
  };

  function money(cents, format) {
    const text = new DOMParser().parseFromString(format, 'text/html').body.textContent;
    return text.replace(/\{\{\s*(\w+)\s*\}\}/, (match, style) => {
      const [decimals, thousands, decimal] = MONEY_STYLES[style] ?? MONEY_STYLES.amount;
      const [whole, fraction] = (Math.round(cents) / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+$)/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    });
  }

  // Refills a c-price rendered with live: true; the money format comes from that snippet, nowhere else.
  function updatePrice(element, { price, compareAtPrice = 0, savings = 0 }) {
    const format = element.dataset.moneyFormat;
    const part = (name) => element.querySelector(`[data-price-part="${name}"]`);
    const onSale = compareAtPrice > price;
    part('current').textContent = money(price, format);
    part('compare').textContent = money(compareAtPrice, format);
    for (const name of ['sale', 'regular', 'compare']) part(name).hidden = !onSale;
    part('savings').textContent = part('savings').dataset.template.replace('[amount]', money(savings, format));
    part('savings').hidden = savings <= 0;
    element.classList.toggle('c-price--sale', onSale);
  }

  // The line markers the discount function reads and the cart attributes function and checkout read (markers.json of the
  // contract), written by scripts only through here (forms through snippets/cart-marker).
  const MARKERS = { bundle: '_bundle', bundle_group: '_bundle_group', gift: '_gift', offer: '_offer', upsell: '_upsell' };
  const ATTRIBUTES = { offer: '_offer', profile: '_profile', device: '_device' };
  function fields(list, kind) {
    return (values) => {
      const written = {};
      for (const [name, value] of Object.entries(values)) {
        if (!Object.hasOwn(list, name)) throw new Error(`"${name}" is no ${kind}`);
        written[list[name]] = String(value);
      }
      return written;
    };
  }
  const markers = fields(MARKERS, 'cart marker');
  const attributes = fields(ATTRIBUTES, 'cart attribute');

  // Cart markup always comes from Liquid: every cart call asks Shopify to render the sections on the page along with it.
  const sectionIds = () => [...new Set([...document.querySelectorAll('[data-cart-section]')].map((element) => element.dataset.cartSection))].slice(0, 5);

  function sectionParams() {
    const ids = sectionIds();
    return ids.length === 0 ? {} : { sections: ids.join(','), sections_url: window.location.pathname };
  }

  function renderSections(sections) {
    // A section may carry several parts that render anew (the rows, the count beside the title); they pair up in order.
    const seen = {};
    const parsed = {};
    for (const element of document.querySelectorAll('[data-cart-section]')) {
      const id = element.dataset.cartSection;
      const html = sections?.[id];
      if (typeof html !== 'string') continue;
      parsed[id] ??= [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-cart-section]')].filter((candidate) => candidate.dataset.cartSection === id);
      seen[id] = (seen[id] ?? -1) + 1;
      const fresh = parsed[id][seen[id]];
      if (!fresh) continue;
      const focused = element.contains(document.activeElement) ? document.activeElement.id : null;
      element.replaceChildren(...fresh.childNodes);
      if (focused !== null) (document.getElementById(focused) ?? element.querySelector('[data-cart-focus]'))?.focus();
    }
  }

  // One call at a time, so a note saved on blur never races the quantity change behind it.
  let queue = Promise.resolve();
  function mutate(url, payload, source) {
    const run = async () => {
      const cart = await request(`${url}.js`, { method: 'POST', body: JSON.stringify({ ...payload, ...sectionParams() }) }, true);
      renderSections(cart.sections);
      theme.emit('cart:updated', { cart, source });
      return cart;
    };
    queue = queue.then(run, run);
    return queue;
  }

  const theme = {
    CartError,
    price: { update: updatePrice },
    // The one money formatter of the theme; a ported brand section asks for it by this name.
    formatMoney: money,
    markers,
    attributes,
    on(name, handler) {
      document.addEventListener(name, handler);
      return () => document.removeEventListener(name, handler);
    },
    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    },
    cart: {
      get() {
        return request(`${cartUrl}.js`);
      },
      async add(payload, source = 'product-form') {
        // FormData posts one line; a plain object ({ items: [...] }) posts several at once as JSON.
        const json = !(payload instanceof FormData);
        if (json) payload = { ...payload, ...sectionParams() };
        else for (const [name, value] of Object.entries(sectionParams())) payload.set(name, value);
        const added = await request(`${cartAddUrl}.js`, { method: 'POST', body: json ? JSON.stringify(payload) : payload }, json);
        renderSections(added.sections);
        // The line is in the cart by now; a failed read must not look like a failed add.
        const cart = await theme.cart.get().catch(() => null);
        theme.emit('cart:updated', { cart, added, source });
        return cart;
      },
      // id is the key of a line; quantity 0 removes it.
      change({ id, quantity }, source = 'cart') {
        return mutate(cartChangeUrl, { id, quantity }, source);
      },
      // updates maps line keys to quantities (several lines at once, as a box needs it); note sets the cart note.
      update(payload, source = 'cart') {
        return mutate(cartUpdateUrl, payload, source);
      },
      async refresh() {
        const ids = sectionIds();
        if (ids.length === 0) return;
        const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`, { headers: { Accept: 'application/json' } });
        if (response.ok) renderSections(await response.json());
      },
    },
  };

  theme.on('cart:updated', ({ detail }) => {
    if (!detail.cart) return;
    for (const counter of document.querySelectorAll('[data-cart-count]')) {
      counter.textContent = detail.cart.item_count;
      counter.hidden = detail.cart.item_count === 0;
    }
  });

  window.theme = theme;
})();
